import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Calendar, Users, Volume2, VolumeX } from 'lucide-react';
import { heroSlides as defaultHeroSlides } from '../mock';
import { publicApi } from '../lib/api';
import { useReservation } from '../context/ReservationContext';

const Hero = () => {
  const { openReservation } = useReservation();
  const [videoSettings, setVideoSettings] = useState({ hero_video_enabled: false, hero_video_url: null, hero_video_poster: null, hero_video_title: 'Promos vacances · Bénin' });
  const [slides, setSlides] = useState(defaultHeroSlides);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    publicApi.getSettings().then((s) => {
      setVideoSettings(s);
      if (Array.isArray(s.hero_slides) && s.hero_slides.length > 0) {
        setSlides(s.hero_slides.map((sl, idx) => ({ id: idx + 1, ...sl })));
      }
    }).catch(() => {});
  }, []);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (videoRef.current) {
      videoRef.current.muted = next;
      if (!next) {
        videoRef.current.play().catch(() => {});
      }
    }
  };
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const t = setInterval(() => nextSlide(), 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, slides.length]);

  const changeSlide = (idx) => {
    if (isAnimating || idx === current) return;
    setIsAnimating(true);
    setCurrent(idx);
    setTimeout(() => setIsAnimating(false), 700);
  };

  const nextSlide = () => changeSlide((current + 1) % slides.length);
  const prevSlide = () => changeSlide((current - 1 + slides.length) % slides.length);

  const safeCurrent = Math.min(current, slides.length - 1);
  const slide = slides[safeCurrent] || slides[0];

  return (
    <section id="accueil" className="relative w-full pt-24">
      <div className="relative w-full h-[80vh] min-h-[600px] overflow-hidden">
        {/* Image carousel background */}
        {slides.map((s, i) => (
          <div
            key={s.id ?? i}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <img
              src={s.image}
              alt={s.title}
              className={`w-full h-full object-cover transition-transform duration-[8000ms] ease-out ${
                i === current ? 'scale-110' : 'scale-100'
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/20 to-slate-900/60"></div>
          </div>
        ))}

        {/* Floating video bottom-right - TikTok/Reels portrait format */}
        {videoSettings.hero_video_enabled && videoSettings.hero_video_url && (
          <div className="absolute top-[calc(50%+3rem)] right-4 md:right-8 z-30 w-24 md:w-36">
            <div className="w-full rounded-2xl overflow-hidden shadow-2xl border-4 border-white/90 hover:scale-105 transition-transform duration-500 group">
              <div className="relative aspect-[9/16] bg-slate-900">
                <video
                  ref={videoRef}
                  key={videoSettings.hero_video_url}
                  className="w-full h-full object-cover"
                  autoPlay muted={muted} loop playsInline preload="auto"
                  poster={videoSettings.hero_video_poster || undefined}
                >
                  <source src={videoSettings.hero_video_url} type="video/mp4" />
                </video>
                <div className="absolute top-2 left-2 flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest z-10">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
                </div>
                <button
                  onClick={toggleMute}
                  data-testid="hero-video-mute-toggle"
                  aria-label={muted ? 'Activer le son' : 'Couper le son'}
                  className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-sm flex items-center justify-center text-white transition-all hover:scale-110"
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                {/* Title overlay on vignette */}
                {videoSettings.hero_video_title && (
                  <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/85 via-black/60 to-transparent pt-6 pb-2 px-2">
                    <div
                      data-testid="hero-video-title"
                      className="text-white text-[10px] md:text-xs font-bold text-center leading-tight drop-shadow-lg"
                    >
                      {videoSettings.hero_video_title}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="relative z-20 h-full flex flex-col items-center justify-center px-6">
          {/* Dots */}
          <div className="flex items-center gap-2 mb-8">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => changeSlide(i)}
                className={`transition-all duration-500 rounded-full ${
                  i === current
                    ? 'w-3 h-3 bg-[#F4A340] scale-110'
                    : 'w-2.5 h-2.5 bg-white/60 hover:bg-white'
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>

          <h1 key={slide.id} className="text-center max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-700">
            <span className="block text-5xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight">
              {slide.title}
            </span>
            <span className="block text-5xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight mt-1">
              <span className="relative inline-block">
                {slide.titleAccent}
                <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 300 12" fill="none">
                  <path d="M2 8C50 3 150 3 298 8" stroke="#F4A340" strokeWidth="4" strokeLinecap="round" />
                </svg>
              </span>
            </span>
          </h1>
          <p className="text-center text-white/90 mt-8 text-lg max-w-xl animate-in fade-in duration-700 delay-200">
            {slide.subtitle}
          </p>

          <a
            onClick={(e) => { e.preventDefault(); openReservation(); }}
            href="#balades"
            className="mt-10 group inline-flex items-center gap-3 px-10 py-4 bg-[#F4A340] hover:bg-[#E89534] text-white font-semibold rounded-full shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
          >
            En savoir plus
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-14 h-14 rounded-full bg-white/95 hover:bg-white shadow-lg flex items-center justify-center hover:scale-110 transition-all duration-300"
          aria-label="Slide précédent"
        >
          <ChevronLeft className="w-6 h-6 text-slate-800" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-14 h-14 rounded-full bg-white/95 hover:bg-white shadow-lg flex items-center justify-center hover:scale-110 transition-all duration-300"
          aria-label="Slide suivant"
        >
          <ChevronRight className="w-6 h-6 text-slate-800" />
        </button>

        {/* Curve bottom */}
        <div className="absolute -bottom-1 left-0 right-0 z-20">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="w-full h-16">
            <path d="M0,60 Q720,120 1440,60 L1440,100 L0,100 Z" fill="#FAF7F2" />
          </svg>
        </div>

        {/* Floating diamond icon */}
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 z-30">
          <div className="w-14 h-14 rotate-45 bg-[#F4A340] rounded-md flex items-center justify-center shadow-xl">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
              <span className="-rotate-45 text-slate-900 font-bold text-xl italic">p</span>
            </div>
          </div>
        </div>
      </div>

      {/* Search / booking bar */}
      <div className="relative -mt-2 bg-[#FAF7F2] pt-16 pb-12 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-[#E89534] font-semibold text-2xl md:text-3xl mb-6">Je veux embarquer</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl mx-auto p-2 bg-white rounded-full shadow-md border-2 border-[#F4A340]/30 hover:border-[#F4A340] transition-all">
            <div className="flex items-center gap-3 px-5 py-3 rounded-full hover:bg-orange-50/50 transition-colors cursor-pointer group">
              <Calendar className="w-5 h-5 text-[#E89534] flex-shrink-0" />
              <div className="text-left flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Date</div>
                <input type="text" placeholder="Choisir une date" className="w-full text-sm outline-none bg-transparent placeholder:text-slate-400" />
              </div>
            </div>
            <div className="flex items-center gap-3 px-5 py-3 rounded-full hover:bg-orange-50/50 transition-colors cursor-pointer group md:border-l md:border-r border-slate-100">
              <Users className="w-5 h-5 text-[#E89534] flex-shrink-0" />
              <div className="text-left flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Passagers</div>
                <input type="text" placeholder="2 personnes" className="w-full text-sm outline-none bg-transparent placeholder:text-slate-400" />
              </div>
            </div>
            <div className="flex items-center gap-2 px-2 py-1">
              <div className="flex items-center gap-3 px-3 py-2 flex-1">
                <MapPin className="w-5 h-5 text-[#E89534] flex-shrink-0" />
                <div className="text-left flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Formule</div>
                  <input type="text" placeholder="Essentielle, Avancée..." className="w-full text-sm outline-none bg-transparent placeholder:text-slate-400" />
                </div>
              </div>
              <button onClick={() => openReservation()} className="px-6 py-3 bg-[#F4A340] hover:bg-[#E89534] text-white font-semibold rounded-full transition-colors whitespace-nowrap">
                Rechercher
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
